import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Phone, Save, Smartphone, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  getAccount,
  getAllAccountSites,
  updateAccountSettings,
} from '../api';
import type { ContactRoleEntry } from '../api';
import { useAccountContactRoles } from '../hooks/useSubscriptionManagers';

const UNASSIGNED = '_unassigned';

const PAYMENT_TERMS = [
  { value: 'NET15', label: 'Net 15' },
  { value: 'NET30', label: 'Net 30' },
  { value: 'NET45', label: 'Net 45' },
  { value: 'NET60', label: 'Net 60' },
  { value: 'NET90', label: 'Net 90' },
  { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
] as const;

const PAYMENT_METHODS = [
  { value: 'cheque', label: 'Cheque' },
  { value: 'eft', label: 'EFT / Direct Deposit' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'autre', label: 'Other' },
] as const;

function contactLabel(c: ContactRoleEntry): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return name || c.email || 'Unknown';
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function EmailChipInput({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [input, setInput] = useState('');

  const addEmail = (raw: string) => {
    const trimmed = raw.trim().toLowerCase();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      toast.error('Invalid email', { description: trimmed });
      return;
    }
    if (value.includes(trimmed)) {
      setInput('');
      return;
    }
    onChange([...value, trimmed]);
    setInput('');
  };

  const removeEmail = (email: string) => {
    onChange(value.filter((e) => e !== email));
  };

  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2">
      {value.map((email) => (
        <Badge key={email} variant="secondary" className="gap-1 text-xs font-normal">
          {email}
          {!disabled && (
            <button
              type="button"
              onClick={() => removeEmail(email)}
              className="rounded-full hover:bg-muted-foreground/20"
              aria-label={`Remove ${email}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}
      <Input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            addEmail(input);
          } else if (e.key === 'Backspace' && !input && value.length > 0) {
            removeEmail(value[value.length - 1]);
          }
        }}
        onBlur={() => {
          if (input.trim()) addEmail(input);
        }}
        placeholder={value.length === 0 ? 'ap@customer.com' : ''}
        disabled={disabled}
        className="h-auto min-w-[160px] flex-1 border-0 p-0 text-sm shadow-none focus-visible:ring-0"
      />
    </div>
  );
}

function ContractualManagerReadOnly({ contact }: { contact: ContactRoleEntry | null }) {
  if (!contact) {
    return (
      <div className="rounded-md border border-input bg-muted/30 px-3 py-2">
        <span className="text-xs italic text-muted-foreground">
          No Contractual Manager set — assign one from the Overview tab.
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-input bg-muted/30 px-3 py-2">
      <div className="flex items-baseline gap-1.5">
        <span className="truncate text-sm font-medium">{contactLabel(contact)}</span>
        {contact.title && (
          <span className="truncate text-[11px] text-muted-foreground">· {contact.title}</span>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
          >
            <Mail className="h-3 w-3" />
            <span className="truncate max-w-[200px]">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
          >
            <Phone className="h-3 w-3" />
            <span>{contact.phone}</span>
          </a>
        )}
        {contact.mobile_phone && (
          <a
            href={`tel:${contact.mobile_phone}`}
            className="inline-flex items-center gap-1 hover:text-atek-blue"
          >
            <Smartphone className="h-3 w-3" />
            <span>{contact.mobile_phone}</span>
          </a>
        )}
      </div>
    </div>
  );
}

export function BillingCard({ accountId }: { accountId: string }) {
  const queryClient = useQueryClient();

  const { data: account, isLoading } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccount(accountId, { includeArchived: true }),
  });

  const { data: contactRoles } = useAccountContactRoles(accountId);

  const { data: sites = [] } = useQuery({
    // The billing-site dropdown needs the WHOLE list: one page of 100 hid the
    // rest on accounts above that (#1237).
    queryKey: ['account-sites-for-billing', accountId],
    queryFn: () => getAllAccountSites(accountId),
  });

  // Local form state — initialized from account when it loads.
  const [billingSiteId, setBillingSiteId] = useState<string | null>(null);
  const [billingAddress, setBillingAddress] = useState('');
  const [paymentTerms, setPaymentTerms] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [apEmails, setApEmails] = useState<string[]>([]);

  useEffect(() => {
    if (account) {
      setBillingSiteId(account.billing_site_id ?? null);
      setBillingAddress(account.billing_address ?? '');
      setPaymentTerms(account.payment_terms ?? null);
      setPaymentMethod(account.payment_method ?? null);
      setApEmails(account.ap_contact_emails ?? []);
    }
  }, [account]);

  const mutation = useMutation({
    mutationFn: () =>
      updateAccountSettings(accountId, {
        billingSiteId,
        billingAddress: billingAddress.trim() || null,
        paymentTerms,
        paymentMethod,
        apContactEmails: apEmails,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      toast.success('Billing settings saved');
    },
    onError: (error) => {
      toast.error('Failed to save billing settings', {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const billingManager = contactRoles?.contractual_manager?.[0] ?? null;

  const isDirty = useMemo(() => {
    if (!account) return false;
    const accountEmails = account.ap_contact_emails ?? [];
    const sameEmails =
      accountEmails.length === apEmails.length &&
      accountEmails.every((e, i) => e === apEmails[i]);
    return (
      billingSiteId !== (account.billing_site_id ?? null) ||
      billingAddress !== (account.billing_address ?? '') ||
      paymentTerms !== (account.payment_terms ?? null) ||
      paymentMethod !== (account.payment_method ?? null) ||
      !sameEmails
    );
  }, [account, billingSiteId, billingAddress, paymentTerms, paymentMethod, apEmails]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Billing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label>Billing Manager</Label>
          <ContractualManagerReadOnly contact={billingManager} />
          <p className="text-xs text-muted-foreground">
            The Contractual Manager is treated as the Billing Manager. Change them from the
            Overview tab.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ap-emails">Account Payable Contact Emails</Label>
          <EmailChipInput
            value={apEmails}
            onChange={setApEmails}
            disabled={mutation.isPending}
          />
          <p className="text-xs text-muted-foreground">
            Where ATEK sends invoices and dunning notices. Press Enter, comma or space to add.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Billing Site</Label>
          <Select
            value={billingSiteId ?? UNASSIGNED}
            onValueChange={(v) => {
              if (v === '') return;
              setBillingSiteId(v === UNASSIGNED ? null : v);
            }}
            disabled={mutation.isPending}
          >
            <SelectTrigger>
              <SelectValue placeholder="No billing site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>None — use billing address below</SelectItem>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                  {site.city ? ` — ${site.city}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Pick the delivery site invoices should be addressed to.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="billing-address">Billing Address</Label>
          <Textarea
            id="billing-address"
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
            placeholder="Used when the billing site doesn't apply (HQ corporate office, etc.)"
            rows={3}
            disabled={mutation.isPending}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Payment Terms</Label>
            <Select
              value={paymentTerms ?? UNASSIGNED}
              onValueChange={(v) => {
                if (v === '') return;
                setPaymentTerms(v === UNASSIGNED ? null : v);
              }}
              disabled={mutation.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Not set" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Not set</SelectItem>
                {PAYMENT_TERMS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Payment Method</Label>
            <Select
              value={paymentMethod ?? UNASSIGNED}
              onValueChange={(v) => {
                if (v === '') return;
                setPaymentMethod(v === UNASSIGNED ? null : v);
              }}
              disabled={mutation.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Not set" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Not set</SelectItem>
                {PAYMENT_METHODS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            disabled={mutation.isPending || !isDirty}
            size="sm"
            onClick={() => mutation.mutate()}
          >
            <Save className="mr-1.5 h-4 w-4" />
            {mutation.isPending ? 'Saving...' : 'Save Billing Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
